import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Router, Search, Wifi } from "lucide-react";
import {
  ApiError,
  type WispNasDto,
  type WispPlanDto,
  type WispStatus,
  type WispSubscriberRowDto,
} from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";
import { ServersTab } from "./ServersTab";
import { SmsTab } from "./SmsTab";
import { ImportPanel } from "./ImportPanel";

const MODULE_KEY = "wisp_radius";
const inputCls = "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const labelCls = "mb-1 block text-xs font-medium text-slate-600";

function errMsg(err: unknown, fallback: string) {
  if (err instanceof ApiError) {
    const m = err.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return fallback;
}

function fmtDateTime(v: string | null) {
  if (!v) return "—";
  return new Date(v).toLocaleString("sr-Latn-RS", { dateStyle: "short", timeStyle: "short" });
}

function fmtBytes(b: number) {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const STATUS_LABEL: Record<WispStatus, string> = {
  ACTIVE: "Aktivan",
  SUSPENDED: "Suspendovan",
  DISABLED: "Isključen",
};
const STATUS_CLS: Record<WispStatus, string> = {
  ACTIVE: "bg-emerald-50 text-emerald-700",
  SUSPENDED: "bg-amber-50 text-amber-800",
  DISABLED: "bg-slate-100 text-slate-600",
};

function StatusPill({ status }: { status: WispStatus }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLS[status]}`}>{STATUS_LABEL[status]}</span>;
}

type Tab = "subscribers" | "plans" | "nas" | "servers" | "sms";

export function WispPage() {
  const [tab, setTab] = useState<Tab>("subscribers");
  const overview = useQuery({ queryKey: ["wisp-overview"], queryFn: () => api.getWispOverview(), retry: false });

  if (overview.isError) {
    if (overview.error instanceof ApiError && overview.error.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={overview.error} moduleKey={MODULE_KEY} />;
    }
    return <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(overview.error, "Učitavanje nije uspelo.")}</p>;
  }
  if (!overview.data) return <Skeleton className="h-40 w-full" />;
  const o = overview.data;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Internet korisnici (WISP)</h1>
        <p className="text-sm text-slate-500">
          Pretplatnici se prijavljuju na MikroTik (PPPoE), a ruter pita HAFiskal da li sme da ih pusti i kojom brzinom.
          Suspendovan korisnik nema internet; čim ga aktiviraš, veza mu se odmah vraća.
        </p>
      </div>

      {!o.radiusReady && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Još nemaš spreman RADIUS server. Dodaj ga u kartici „RADIUS serveri" i pokreni komandu za instalaciju na njemu - posle toga dodaješ
          rutere. Pretplatnike i pakete možeš da unosiš odmah.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {[
          ["Aktivni", o.active],
          ["Suspendovani", o.suspended],
          ["Isključeni", o.disabled],
          ["Trenutno na mreži", o.online],
          ["Rutera / servera", `${o.nas} / ${o.servers}`],
        ].map(([label, v]) => (
          <div key={String(label)} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-xl font-semibold text-slate-900">{v}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-1 border-b border-border">
        {(
          [
            ["subscribers", "Pretplatnici"],
            ["plans", "Paketi"],
            ["nas", "Ruteri"],
            ["servers", "RADIUS serveri"],
            ["sms", "SMS"],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === key ? "border-brand text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "subscribers" && <SubscribersTab />}
      {tab === "plans" && <PlansTab />}
      {tab === "nas" && <NasTab />}
      {tab === "servers" && <ServersTab />}
      {tab === "sms" && <SmsTab />}
    </div>
  );
}

// --- Pretplatnici ------------------------------------------------------------

function SubscribersTab() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const list = useQuery({
    queryKey: ["wisp-subscribers", search, status],
    queryFn: () => api.listWispSubscribers({ search: search || undefined, status: status || undefined }),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <input
            className={`${inputCls} pl-9`}
            placeholder="Ime, telefon, adresa ili korisničko ime"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className={`${inputCls} w-44`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Svi</option>
          <option value="ACTIVE">Aktivni</option>
          <option value="SUSPENDED">Suspendovani</option>
          <option value="DISABLED">Isključeni</option>
        </select>
        <Button onClick={() => setAdding(true)}>
          <Plus className="mr-1 h-4 w-4" /> Nov pretplatnik
        </Button>
        <Button variant="secondary" onClick={() => setImporting(true)}>
          Uvezi iz starog sistema
        </Button>
      </div>

      {importing && <ImportPanel onClose={() => setImporting(false)} />}

      {adding && <NewSubscriber onClose={() => setAdding(false)} onCreated={(id) => { setAdding(false); setOpenId(id); }} />}

      {list.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !list.data?.length ? (
        <EmptyState icon={Wifi} title="Nema pretplatnika" description="Dodaj prvog pretplatnika - dobija korisničko ime i lozinku za PPPoE." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2">Kupac</th>
                <th className="px-3 py-2">Korisničko ime</th>
                <th className="px-3 py-2">Paket</th>
                <th className="px-3 py-2">Stanje</th>
                <th className="px-3 py-2">Na mreži</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((s) => (
                <SubscriberRow key={s.id} s={s} open={openId === s.id} onToggle={() => setOpenId(openId === s.id ? null : s.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SubscriberRow({ s, open, onToggle }: { s: WispSubscriberRowDto; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="cursor-pointer border-t border-border hover:bg-slate-50" onClick={onToggle}>
        <td className="px-3 py-2">
          <div className="font-medium text-slate-900">{s.customerName}</div>
          <div className="text-xs text-slate-500">{[s.phone, s.address].filter(Boolean).join(" · ") || "—"}</div>
        </td>
        <td className="px-3 py-2 font-mono text-xs">{s.username}</td>
        <td className="px-3 py-2">{s.planName}</td>
        <td className="px-3 py-2">
          <StatusPill status={s.status} />
        </td>
        <td className="px-3 py-2 text-xs">
          {s.online ? <span className="text-emerald-700">● {s.framedIp ?? "da"}</span> : <span className="text-slate-400">○ ne</span>}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="bg-slate-50 px-3 py-3">
            <SubscriberDetail id={s.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function SubscriberDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const d = useQuery({ queryKey: ["wisp-subscriber", id], queryFn: () => api.getWispSubscriber(id) });
  const plans = useQuery({ queryKey: ["wisp-plans"], queryFn: () => api.listWispPlans() });
  const [reason, setReason] = useState("");
  const [pw, setPw] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [edit, setEdit] = useState<{ planId: string; staticIp: string; password: string; note: string } | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["wisp-subscriber", id] });
    qc.invalidateQueries({ queryKey: ["wisp-subscribers"] });
    qc.invalidateQueries({ queryKey: ["wisp-overview"] });
  };
  const setStatus = useMutation({
    mutationFn: (st: WispStatus) => api.setWispSubscriberStatus(id, st, reason || undefined),
    onSuccess: () => {
      setReason("");
      setMsg({ ok: true, text: "Sačuvano. Ako je korisnik bio na mreži, veza mu je prekinuta i sam se ponovo povezuje sa novim stanjem." });
      refresh();
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e, "Promena nije uspela.") }),
  });
  const reveal = useMutation({
    mutationFn: () => api.revealWispPassword(id),
    onSuccess: (r) => setPw(r.password),
  });
  const save = useMutation({
    mutationFn: () =>
      api.updateWispSubscriber(id, {
        planId: edit!.planId,
        staticIp: edit!.staticIp || null,
        note: edit!.note || null,
        ...(edit!.password ? { password: edit!.password } : {}),
      }),
    onSuccess: () => {
      setEdit(null);
      setPw(null);
      setMsg({ ok: true, text: "Izmene su sačuvane." });
      refresh();
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e, "Čuvanje nije uspelo.") }),
  });
  const del = useMutation({
    mutationFn: () => api.deleteWispSubscriber(id),
    onSuccess: refresh,
    onError: (e) => setMsg({ ok: false, text: errMsg(e, "Brisanje nije uspelo.") }),
  });

  if (!d.data) return <Skeleton className="h-24 w-full" />;
  const s = d.data;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-lg bg-white p-3 text-sm">
          <div className="text-xs text-slate-500">Prijava na ruter (PPPoE)</div>
          <div>
            Korisnik: <span className="font-mono">{s.username}</span>
          </div>
          <div className="flex items-center gap-2">
            Lozinka: {pw ? <span className="select-all font-mono">{pw}</span> : <button type="button" className="text-brand underline" onClick={() => reveal.mutate()}>prikaži</button>}
          </div>
          {s.staticIp && <div>Fiksna IP: {s.staticIp}</div>}
        </div>
        <div className="rounded-lg bg-white p-3 text-sm">
          <div className="text-xs text-slate-500">Stanje</div>
          <div className="flex items-center gap-2">
            <StatusPill status={s.status} /> od {fmtDateTime(s.statusChangedAt)}
          </div>
          {s.statusReason && <div className="text-slate-600">Razlog: {s.statusReason}</div>}
          <div className="text-xs text-slate-500">
            Poslednja prijava: {fmtDateTime(s.lastAuthAt)} {s.lastAuthResult ? `(${s.lastAuthResult})` : ""}
          </div>
        </div>
        <div className="rounded-lg bg-white p-3 text-sm">
          <div className="text-xs text-slate-500">Kupac</div>
          <div className="font-medium">{s.customer?.name ?? "—"}</div>
          <div className="text-slate-600">{[s.customer?.phone, s.customer?.email].filter(Boolean).join(" · ")}</div>
          <div className="text-slate-600">{[s.customer?.address, s.customer?.city].filter(Boolean).join(", ")}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-lg bg-white p-3">
        <div className="min-w-[220px] flex-1">
          <label className={labelCls}>Razlog (upisuje se u istoriju)</label>
          <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="npr. dug za oktobar" />
        </div>
        {s.status !== "ACTIVE" && (
          <Button onClick={() => setStatus.mutate("ACTIVE")} disabled={setStatus.isPending}>
            Aktiviraj
          </Button>
        )}
        {s.status === "ACTIVE" && (
          <Button variant="secondary" onClick={() => setStatus.mutate("SUSPENDED")} disabled={setStatus.isPending}>
            Suspenduj
          </Button>
        )}
        {s.status !== "DISABLED" && (
          <Button
            variant="secondary"
            onClick={() => window.confirm("Trajno isključiti pretplatnika? Neće moći da se prijavi dok ga ponovo ne aktiviraš.") && setStatus.mutate("DISABLED")}
            disabled={setStatus.isPending}
          >
            Isključi trajno
          </Button>
        )}
        {s.status === "DISABLED" && (
          <Button variant="secondary" onClick={() => window.confirm("Obrisati pretplatnika? Kupac ostaje u HAFiskalu.") && del.mutate()}>
            Obriši
          </Button>
        )}
        {!edit && (
          <Button
            variant="secondary"
            onClick={() => setEdit({ planId: s.planId, staticIp: s.staticIp ?? "", password: "", note: s.note ?? "" })}
          >
            Izmeni
          </Button>
        )}
      </div>

      {edit && (
        <div className="grid gap-3 rounded-lg bg-white p-3 md:grid-cols-4">
          <div>
            <label className={labelCls}>Paket</label>
            <select className={inputCls} value={edit.planId} onChange={(e) => setEdit({ ...edit, planId: e.target.value })}>
              {plans.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Fiksna IP (neobavezno)</label>
            <input className={inputCls} value={edit.staticIp} onChange={(e) => setEdit({ ...edit, staticIp: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Nova lozinka (prazno = ista)</label>
            <input className={inputCls} value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Napomena</label>
            <input className={inputCls} value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} />
          </div>
          <div className="flex gap-2 md:col-span-4">
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              Sačuvaj
            </Button>
            <Button variant="secondary" onClick={() => setEdit(null)}>
              Odustani
            </Button>
          </div>
        </div>
      )}

      {msg && <p className={`rounded-lg px-3 py-2 text-sm ${msg.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>{msg.text}</p>}

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-lg bg-white p-3">
          <div className="mb-1 text-xs font-medium text-slate-500">Istorija stanja</div>
          {s.logs.map((l) => (
            <div key={l.id} className="border-t border-border py-1 text-xs first:border-t-0">
              {fmtDateTime(l.createdAt)} · {l.toStatus in STATUS_LABEL ? STATUS_LABEL[l.toStatus as WispStatus] : l.toStatus}
              {l.reason ? ` · ${l.reason}` : ""} · {l.source === "AUTO" ? "automatski" : (l.userName ?? "ručno")}
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-white p-3">
          <div className="mb-1 text-xs font-medium text-slate-500">Poslednje veze</div>
          {s.sessions.length === 0 && <div className="text-xs text-slate-400">Još se nije povezivao.</div>}
          {s.sessions.map((x) => (
            <div key={x.id} className="border-t border-border py-1 text-xs first:border-t-0">
              {fmtDateTime(x.startedAt)} – {x.stoppedAt ? fmtDateTime(x.stoppedAt) : "traje"} · {x.nasName} · {x.framedIp ?? "—"} · ↓
              {fmtBytes(x.downloadBytes)} ↑{fmtBytes(x.uploadBytes)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NewSubscriber({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ["wisp-plans"], queryFn: () => api.listWispPlans() });
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [customerSearch, setCustomerSearch] = useState("");
  const customers = useQuery({
    queryKey: ["customers-search", customerSearch],
    queryFn: () => api.listCustomers(customerSearch || undefined),
    enabled: mode === "existing",
  });
  const [f, setF] = useState({
    customerId: "",
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    username: "",
    password: "",
    planId: "",
    staticIp: "",
    note: "",
  });
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: string; username: string; password: string } | null>(null);
  const set = (k: keyof typeof f, v: string) => setF({ ...f, [k]: v });

  const create = useMutation({
    mutationFn: () =>
      api.createWispSubscriber({
        ...(mode === "existing"
          ? { customerId: f.customerId }
          : { newCustomer: { name: f.name, phone: f.phone || undefined, email: f.email || undefined, address: f.address || undefined, city: f.city || undefined } }),
        username: f.username,
        password: f.password || undefined,
        planId: f.planId || plans.data?.find((p) => p.active)?.id,
        staticIp: f.staticIp || null,
        note: f.note || null,
      }),
    onSuccess: (r) => {
      setErr(null);
      setCreated(r);
      qc.invalidateQueries({ queryKey: ["wisp-subscribers"] });
      qc.invalidateQueries({ queryKey: ["wisp-overview"] });
    },
    onError: (e) => setErr(errMsg(e, "Dodavanje nije uspelo.")),
  });

  if (created) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        <p className="font-semibold">Pretplatnik je dodat.</p>
        <p>
          Podaci za PPPoE na uređaju kod korisnika: korisnik <span className="select-all font-mono">{created.username}</span>, lozinka{" "}
          <span className="select-all font-mono">{created.password}</span>
        </p>
        <div className="mt-2">
          <Button onClick={() => onCreated(created.id)}>U redu</Button>
        </div>
      </div>
    );
  }

  const activePlans = plans.data?.filter((p) => p.active) ?? [];
  return (
    <div className="space-y-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">Nov pretplatnik</h2>
        <div className="flex gap-2 text-sm">
          <label className="flex items-center gap-1">
            <input type="radio" checked={mode === "new"} onChange={() => setMode("new")} /> nov kupac
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" checked={mode === "existing"} onChange={() => setMode("existing")} /> postojeći kupac
          </label>
        </div>
      </div>

      {activePlans.length === 0 && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">Prvo napravi bar jedan paket (kartica „Paketi").</p>
      )}

      {mode === "new" ? (
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <label className={labelCls}>Ime i prezime / naziv *</label>
            <input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Telefon (za SMS)</label>
            <input className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="064..." />
          </div>
          <div>
            <label className={labelCls}>E-pošta</label>
            <input className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Adresa</label>
            <input className={inputCls} value={f.address} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Mesto</label>
            <input className={inputCls} value={f.city} onChange={(e) => set("city", e.target.value)} />
          </div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className={labelCls}>Pretraga kupca</label>
            <input className={inputCls} value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} placeholder="ime ili PIB" />
          </div>
          <div>
            <label className={labelCls}>Kupac *</label>
            <select className={inputCls} value={f.customerId} onChange={(e) => set("customerId", e.target.value)}>
              <option value="">— izaberi —</option>
              {customers.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-4">
        <div>
          <label className={labelCls}>Korisničko ime (PPPoE) *</label>
          <input className={inputCls} value={f.username} onChange={(e) => set("username", e.target.value.replace(/\s/g, ""))} />
        </div>
        <div>
          <label className={labelCls}>Lozinka (prazno = napravi sam)</label>
          <input className={inputCls} value={f.password} onChange={(e) => set("password", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Paket *</label>
          <select className={inputCls} value={f.planId || activePlans[0]?.id || ""} onChange={(e) => set("planId", e.target.value)}>
            {activePlans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.downloadMbps}/{p.uploadMbps} Mbps)
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Fiksna IP (neobavezno)</label>
          <input className={inputCls} value={f.staticIp} onChange={(e) => set("staticIp", e.target.value)} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Napomena</label>
        <input className={inputCls} value={f.note} onChange={(e) => set("note", e.target.value)} />
      </div>

      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
      <div className="flex gap-2">
        <Button onClick={() => create.mutate()} disabled={create.isPending || activePlans.length === 0}>
          Dodaj
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Odustani
        </Button>
      </div>
    </div>
  );
}

// --- Paketi ---------------------------------------------------------------------

function PlansTab() {
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ["wisp-plans"], queryFn: () => api.listWispPlans() });
  const products = useQuery({ queryKey: ["wisp-products"], queryFn: () => api.listProducts() });
  const [f, setF] = useState({ name: "", down: "", up: "", productId: "" });
  const [err, setErr] = useState<string | null>(null);
  const done = () => {
    setErr(null);
    qc.invalidateQueries({ queryKey: ["wisp-plans"] });
    qc.invalidateQueries({ queryKey: ["wisp-overview"] });
  };
  const onErr = (e: unknown) => setErr(errMsg(e, "Čuvanje nije uspelo."));
  const create = useMutation({
    mutationFn: () =>
      api.createWispPlan({ name: f.name, downloadMbps: Number(f.down.replace(",", ".")), uploadMbps: Number(f.up.replace(",", ".")), productId: f.productId || null }),
    onSuccess: () => {
      setF({ name: "", down: "", up: "", productId: "" });
      done();
    },
    onError: onErr,
  });
  const toggle = useMutation({ mutationFn: (p: WispPlanDto) => api.updateWispPlan(p.id, { active: !p.active }), onSuccess: done, onError: onErr });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteWispPlan(id), onSuccess: done, onError: onErr });

  return (
    <div className="space-y-3">
      <div className="grid gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle md:grid-cols-5">
        <div>
          <label className={labelCls}>Naziv paketa</label>
          <input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="npr. Kućni 20" />
        </div>
        <div>
          <label className={labelCls}>Preuzimanje (Mbps)</label>
          <input className={inputCls} value={f.down} onChange={(e) => setF({ ...f, down: e.target.value })} placeholder="20" />
        </div>
        <div>
          <label className={labelCls}>Slanje (Mbps)</label>
          <input className={inputCls} value={f.up} onChange={(e) => setF({ ...f, up: e.target.value })} placeholder="5" />
        </div>
        <div>
          <label className={labelCls}>Artikal za fakturu (cena)</label>
          <select className={inputCls} value={f.productId} onChange={(e) => setF({ ...f, productId: e.target.value })}>
            <option value="">— bez artikla —</option>
            {products.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <Button onClick={() => create.mutate()} disabled={create.isPending || !f.name || !f.down || !f.up}>
            <Plus className="mr-1 h-4 w-4" /> Dodaj paket
          </Button>
        </div>
      </div>
      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

      {plans.data?.length ? (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2">Paket</th>
                <th className="px-3 py-2">Brzina</th>
                <th className="px-3 py-2">Artikal</th>
                <th className="px-3 py-2">Pretplatnika</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {plans.data.map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">
                    {p.name} {!p.active && <span className="text-xs text-slate-400">(isključen)</span>}
                  </td>
                  <td className="px-3 py-2">
                    ↓ {p.downloadMbps} / ↑ {p.uploadMbps} Mbps
                  </td>
                  <td className="px-3 py-2">{p.productName ?? "—"}</td>
                  <td className="px-3 py-2">{p.subscribers}</td>
                  <td className="space-x-2 px-3 py-2 text-right">
                    <button type="button" className="text-xs text-brand underline" onClick={() => toggle.mutate(p)}>
                      {p.active ? "isključi" : "uključi"}
                    </button>
                    {p.subscribers === 0 && (
                      <button type="button" className="text-xs text-red-600 underline" onClick={() => remove.mutate(p.id)}>
                        obriši
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !plans.isLoading && <EmptyState icon={Wifi} title="Nema paketa" description="Paket određuje brzinu korisnika." />
      )}
    </div>
  );
}

// --- Ruteri -------------------------------------------------------------------------

function NasTab() {
  const qc = useQueryClient();
  const nas = useQuery({ queryKey: ["wisp-nas"], queryFn: () => api.listWispNas() });
  const servers = useQuery({ queryKey: ["wisp-servers"], queryFn: () => api.listWispServers() });
  const [name, setName] = useState("");
  const [serverId, setServerId] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [script, setScript] = useState<{ id: string; text: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const done = () => {
    setErr(null);
    qc.invalidateQueries({ queryKey: ["wisp-nas"] });
    qc.invalidateQueries({ queryKey: ["wisp-overview"] });
  };
  const create = useMutation({
    mutationFn: () => api.createWispNas(name, serverId || servers.data?.[0]?.id || ""),
    onSuccess: () => {
      setName("");
      done();
    },
    onError: (e) => setErr(errMsg(e, "Dodavanje nije uspelo.")),
  });
  const remove = useMutation({ mutationFn: (id: string) => api.deleteWispNas(id), onSuccess: done });
  const showScript = useMutation({
    mutationFn: (id: string) => api.getWispNasScript(id),
    onSuccess: (r, id) => {
      setCopied(false);
      setScript({ id, text: r.script });
    },
    onError: (e) => setErr(errMsg(e, "Skripta nije dostupna.")),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div className="min-w-[240px] flex-1">
          <label className={labelCls}>Naziv rutera (npr. lokacija tornja)</label>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="w-56">
          <label className={labelCls}>RADIUS server</label>
          <select className={inputCls} value={serverId || servers.data?.[0]?.id || ""} onChange={(e) => setServerId(e.target.value)}>
            {servers.data?.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </div>
        <Button onClick={() => create.mutate()} disabled={create.isPending || !name.trim() || !servers.data?.length}>
          <Plus className="mr-1 h-4 w-4" /> Dodaj ruter
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        Za svaki MikroTik (RouterOS 7) klikni „Skripta", kopiraj je i nalepi u Terminal rutera. Skripta sama napravi bezbednu vezu do tvog RADIUS
        servera i uključi prijavu korisnika preko HAFiskal-a. Može da se nalepi ponovo bez štete.
      </p>
      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

      {nas.data?.length ? (
        <div className="space-y-2">
          {nas.data.map((n: WispNasDto) => (
            <div key={n.id} className="rounded-xl border border-border bg-surface p-3 shadow-subtle">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-medium text-slate-900">{n.name}</div>
                  <div className="text-xs text-slate-500">
                    {n.radiusServerName ?? "bez servera"} · adresa u tunelu {n.tunnelIp} · poslednji zahtev {fmtDateTime(n.lastRequestAt)} · na mreži {n.online}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => showScript.mutate(n.id)} disabled={!n.ready}>
                    Skripta
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => window.confirm(`Ukloniti ruter "${n.name}"? Korisnici preko njega više neće moći da se prijave.`) && remove.mutate(n.id)}
                  >
                    Ukloni
                  </Button>
                </div>
              </div>
              {script?.id === n.id && (
                <div className="mt-3">
                  <div className="mb-1 flex justify-end">
                    <button
                      type="button"
                      className="flex items-center gap-1 text-xs text-brand underline"
                      onClick={() => {
                        void navigator.clipboard.writeText(script.text).then(() => setCopied(true));
                      }}
                    >
                      <Copy className="h-3 w-3" /> {copied ? "Kopirano" : "Kopiraj skriptu"}
                    </button>
                  </div>
                  <pre className="max-h-80 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">{script.text}</pre>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        !nas.isLoading && <EmptyState icon={Router} title="Nema rutera" description="Dodaj MikroTik preko koga se korisnici prijavljuju." />
      )}
    </div>
  );
}
